import { z } from 'zod';

import type { ReportFilterField, ReportFilterMode, ReportTimeGranularity } from './reportEnums';
import { ReportDateRangeType, ReportDimension, ReportMetric, ReportType } from './reportEnums';

const reportScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const reportRowSchema = z.record(z.string(), reportScalarSchema);

export const reportFilterSchema = z.looseObject({
  dateRange: z.enum(ReportDateRangeType).nullish(),
  dateFrom: z.string(),
  dateTo: z.string(),
  bidder: z.unknown().optional(),
  widgets: z.array(z.string()).nullish(),
  adUnitIds: z.array(z.string()).nullish(),
  agencyIds: z.array(z.string()).nullish(),
  msBrandIds: z.array(z.string()).nullish(),
  msCampaignIds: z.array(z.string()).nullish(),
  publisherIds: z.array(z.string()).nullish(),
  siteIds: z.array(z.string()).nullish(),
  excludeDfpSourceBidderRevenue: z.boolean().nullish(),
  activityStatuses: z.array(z.string()).nullish(),
  demandPartnerIds: z.array(z.string()).nullish(),
  domains: z.array(z.string()).nullish(),
  iiqArms: z.array(z.string()).nullish(),
  browsers: z.array(z.string()).nullish(),
  placementIds: z.array(z.string()).nullish(),
  embedIds: z.array(z.string()).nullish(),
  coIds: z.array(z.string()).nullish(),
  dpIds: z.array(z.string()).nullish(),
  productTypes: z.array(z.string()).nullish(),
  bundleIds: z.array(z.string()).nullish(),
  networkIds: z.array(z.string()).nullish(),
  platformIds: z.array(z.string()).nullish(),
  countries: z.array(z.string()).nullish(),
  demandIntegrationIds: z.array(z.string()).nullish(),
  exchangeNetworkOnly: z
    .boolean()
    .nullish()
    .transform((value) => value ?? false),
  excludePublisherIds: z.array(z.string()).nullish(),
  excludeDemandIntegrationIds: z.array(z.string()).nullish(),
  excludePlacementIds: z.array(z.string()).nullish(),
  excludeDpIds: z.array(z.string()).nullish(),
  excludeDemandPartnerIds: z.array(z.string()).nullish(),
  publisherSecondaryIds: z.array(z.string()).nullish(),
  dealIds: z.array(z.string()).nullish(),
  orderIds: z.array(z.string()).nullish(),
  lineItemIds: z.array(z.number()).nullish(),
  appIds: z.array(z.string()).nullish(),
  excludeAppIds: z.array(z.string()).nullish(),
  excludeDomains: z.array(z.string()).nullish(),
  topnMetric: z.enum(ReportMetric).nullish(),
  topnRecords: z.number().nullish(),
});

export const reportRequestSchema = z.object({
  metrics: z.array(z.enum(ReportMetric)),
  dimensions: z.array(z.enum(ReportDimension)),
  filters: reportFilterSchema,
  source: z
    .enum(ReportType)
    .nullish()
    .transform((value) => value ?? ReportType.Exchange),
});

const highCardinalityReportMetaSchema = z.looseObject({
  limited: z.boolean().nullish(),
  limitedRows: z.number().nullish(),
  exportedRows: z.number().nullish(),
  downloadUrl: z.string().nullish(),
  exportBucket: z.string().nullish(),
  exportKey: z.string().nullish(),
  errorMessage: z.string().nullish(),
});

export const reportResponseSchema = z.looseObject({
  report: z.array(reportRowSchema),
  total: reportRowSchema.nullish(),
  highCardinalityReportMeta: highCardinalityReportMetaSchema.nullish(),
});

export type ReportFilter = z.infer<typeof reportFilterSchema>;
export type ReportRequest = z.infer<typeof reportRequestSchema>;
export type ReportResponse = z.infer<typeof reportResponseSchema>;

export interface ReportExplorerConfiguration {
  dateFrom: string;
  dateTo: string;
  dateRange: ReportDateRangeType | null;
  tableDimensions: ReportDimension[];
  tableMetrics: ReportMetric[];
  chartGranularity: ReportTimeGranularity;
  chartMetrics: ReportMetric[];
  timeShiftDays: number | null;
  reportFilters: ReportExplorerFilter[];
  passthroughFilters?: Partial<ReportFilter>;
}

export interface ReportFilterOption {
  id: string;
  label: string;
}

export interface ReportShareUserOption extends ReportFilterOption {
  email: string;
}

export interface ReportExplorerFilter {
  id: string;
  field: ReportFilterField;
  mode: ReportFilterMode;
  values: ReportFilterOption[];
}

export const reportPageSizes = [5, 10, 25, 50, 100] as const;
